import type { MouseEvent, ReactNode } from "react";
import { Link } from "@tanstack/react-router";

/* ──────────────────────────────────────────────────────────────────────────
   Pieces the Signet-styled reporting surfaces share: the connections page
   and the home cards. Reporting's own views are the legacy page
   (legacy/page.tsx) and draw nothing from here.
   ────────────────────────────────────────────────────────────────────────── */

/** A link to a reporting route. Module routes are registered at runtime, so the router's typed paths do not know them. */
export function RLink({
  to,
  search,
  className,
  children,
  onClick,
  title,
  ...aria
}: {
  to: string;
  search?: Record<string, string | undefined>;
  className?: string;
  children: ReactNode;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  title?: string;
  "aria-label"?: string;
  "aria-current"?: "page";
}) {
  return (
    <Link to={to as never} search={search as never} className={className} onClick={onClick} title={title} {...aria}>
      {children}
    </Link>
  );
}

export function ReportingPage({ title, intro, children }: { title: ReactNode; intro?: ReactNode; children: ReactNode }) {
  return (
    <div className="page rp-page">
      <header className="page-head">
        <div style={{ minWidth: 0 }}>
          <RLink to="/reporting" className="rp-back">Reporting</RLink>
          <h1>{title}</h1>
          {intro ? <p>{intro}</p> : null}
        </div>
      </header>
      {children}
    </div>
  );
}

export function messageOf(error: unknown, fallback = "Something went wrong. Try again in a moment."): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

export function Delta({ value, suffix }: { value: number | null; suffix?: string }) {
  if (value === null || !Number.isFinite(value)) return <span className="rp-delta flat">No prior period</span>;
  const up = value >= 0;
  return (
    <span className={`rp-delta ${up ? "up" : "down"}`}>
      {up ? "▲" : "▼"} {Math.abs(value).toFixed(1)}%{suffix ? ` ${suffix}` : ""}
    </span>
  );
}
